// Copyright (c) 2025 WiseTime. All rights reserved.

import { Action } from "redux"
import { EMPTY, Observable, of } from "rxjs"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import * as Slice from "../slice"
import * as Actions from "../actions"
import { convertPbDateToTimelineDate, getTimelineDatesInRange } from "../../timeline/dateTime"
import { executor, Init, userTimelineMessageService } from "../../../app/rpc"
import {
  ListUserTimelineMessagesRequestSchema,
  ListUserTimelineMessagesResponse,
} from "../../../generated/wisetime/user/timelinemessage/v4/user_timeline_message_pb"
import { DateSchema } from "../../../generated/google/type/date_pb"

type ListUserTimelineMessagesRpc = (request: Init<typeof ListUserTimelineMessagesRequestSchema>) =>
  Observable<ListUserTimelineMessagesResponse>

export const listUserTimelineMessages = (listUserTimelineMessagesRpc: ListUserTimelineMessagesRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.listUserTimelineMessages.match),
      mergeMap(({ payload }) =>
        of(payload)
          .pipe(
            mergeMap(listUserTimelineMessagesRpc),
            mergeMap(response => {
              const { startDate, endDate, siteId } = payload
              const { timelineMessages } = response
              if (startDate === undefined || endDate === undefined || siteId === undefined) {
                return EMPTY
              }
              const result: Slice.SetTimelineMessagesPayload[] = getTimelineDatesInRange(
                convertPbDateToTimelineDate(create(DateSchema, startDate)),
                convertPbDateToTimelineDate(create(DateSchema, endDate)),
              ).map(date => ({
                cacheKey: Slice.timelineMessagesCacheKey(date, siteId),
                timelineMessagesList: timelineMessages.filter(m => (
                  m.date && convertPbDateToTimelineDate(m.date) === date
                )),
              }))
              return of(Slice.setTimelineMessages(result))
            }),
            catchError(_ => of(Actions.listUserTimelineMessagesFailed()))
          )
      )
    )

const listUserTimelineMessagesRpc = (
  request: Init<typeof ListUserTimelineMessagesRequestSchema>,
): Observable<ListUserTimelineMessagesResponse> =>
  executor(() => userTimelineMessageService.listUserTimelineMessages(
    create(ListUserTimelineMessagesRequestSchema, request),
  ))

export default listUserTimelineMessages(listUserTimelineMessagesRpc)
